import React, { useEffect, useRef } from 'react';
import ControlledInput from 'src/components/ui/Input/Input';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { usePublicChatCreate } from 'src/services/customerTracking';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import Button, { ButtonPositionProps, ButtonToneProps } from 'src/components/ui/Button/Button';
import ControlledTextarea from 'src/components/ui/Textarea/Textarea';
import ContainerOverlay from 'src/components/ui/ContainerOverlay/ContainerOverlay';
import { useOutsideClick, useEventListeners } from 'src/hooks/utils';
import ContainerHead from 'src/components/ui/ContainerOverlay/ContainerHead/ContainerHead';
import styles from './CreateSupportTicketOverlay.module.scss';

export interface CreateSupportTicketOverlayProps {
  onClose: () => void;
}

interface SupportTicketFormData {
  title: string;
  text: string;
}

const useSupportTicketForm = (onSuccess: () => void) => {
  const { t } = useTranslation();
  const { trackingKey } = useParams();
  const createChatMutation = usePublicChatCreate();

  const supportTicketSchema = z.object({
    title: z.string().min(1, t('general.title_required')),
    text: z.string().min(1, t('general.message_required')),
  });

  const form = useForm<SupportTicketFormData>({
    resolver: zodResolver(supportTicketSchema),
    defaultValues: {
      title: '',
      text: '',
    },
  });

  const handleSubmit = async (data: SupportTicketFormData) => {
    try {
      if (!trackingKey) {
        throw new Error(`Error submitting ticket: trackingKey is undefined`);
      }
      await createChatMutation.mutateAsync({
        params: {
          path: {
            key: trackingKey,
          },
        },
        body: {
          message: `title: ${data.title}; message: ${data.text}`,
          is_case: true,
        },
      });
      toast.success(t('success.support_ticket_created'));
      onSuccess();
    } catch (error) {
      toast.error(t('errors.support_ticket_create_failed'));
    }
  };

  return {
    control: form.control,
    handleSubmit: form.handleSubmit(handleSubmit),
    errors: form.formState.errors,
  };
};

const CreateSupportTicketOverlay: React.FC<CreateSupportTicketOverlayProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const mainRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const { handleSubmit, errors, control } = useSupportTicketForm(() => onClose());

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  useOutsideClick(mainRef, (e: MouseEvent) => {
    if (mainRef.current && !mainRef.current.contains(e.target as Node)) {
      onClose();
    }
  });

  useEventListeners([{ event: 'keydown' }], (event: Event) => {
    if ((event as KeyboardEvent).key === 'Escape') {
      onClose();
    }
  });

  return (
    <ContainerOverlay isExpanded ref={mainRef} className={styles.root}>
      <ContainerHead>
        <div className="flex justify-between items-center w-full">
          <Button onClick={onClose} icon="close" tone={ButtonToneProps.GHOST} />
          <h2 className="typo-text-16--bold">{t('tracking.create_support_ticket')}</h2>
          <span />
        </div>
      </ContainerHead>
      <div className={styles.container}>
        <h3 className="typo-title-36">{t('support_ticket.your_message')}</h3>
        <form onSubmit={handleSubmit}>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <ControlledInput
                {...field}
                ref={firstInputRef}
                type="string"
                label={t('support_ticket.subject')}
                placeholder=""
                className="mb-16"
                error={!!errors.title}
                errorMessage={errors.title?.message}
                maxLength={140}
              />
            )}
          />
          <Controller
            name="text"
            control={control}
            render={({ field }) => (
              <ControlledTextarea
                {...field}
                placeholder={t('support_ticket.message')}
                maxLength={2000}
                error={!!errors.text}
                errorMessage={errors.text?.message}
                className="mb-16"
              />
            )}
          />
          <Button
            type="submit"
            tone={ButtonToneProps.PRIMARY_BLUE}
            title={t('support_ticket.send')}
            icon="send"
            iconPosition={ButtonPositionProps.AFTER}
          />
        </form>
      </div>
    </ContainerOverlay>
  );
};

export default CreateSupportTicketOverlay;
